import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  FormGroup,
  TextField,
  Typography,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import ElementField from "./element-field";
import {
  formElementTypes,
  setQCReviewIsEdit,
} from "../../../../utils/Constant";
import MultiInputPreview from "../../../admin/form-templates/form-builder/preview/multi-input";
import QuestionTable from "../../../admin/form-templates/form-builder/preview/table";
import InspectorPhotosList from "./inspector-photos-list";
import {
  deleteElementAnswerRequest,
  elementApprovalRequest,
  updateElementAnswerRequest,
  uploadTempFileRequest,
} from "../../../../utils/APICalls";
import { getOrderInspectionFormRequest } from "../../../../redux/orders/slice";
import { useDispatch } from "react-redux";
import ErrorNotification from "../../../../components/error-notification/error-notification";
import Loading from "../../../../components/loading/loading";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import { RichTextReadOnly } from "mui-tiptap";
import useExtensions from "../../../../components/text-editor/use-extensions";

export default function QCReviewSectionElement({
  element,
  orderId,
  qc2Completed,
}: any) {
  const dispatch = useDispatch();
  const extensions = useExtensions();
  const [isEdit, setIsEdit] = useState(false);
  const [canTakePhotos, setCanTakePhotos] = useState(false);
  const [canQCTakePhotos, setCanQCTakePhotos] = useState(false);
  const [qcReason, setQCReason] = useState("");
  const [photos, setPhotos] = useState<any[]>([]);
  const [qcPhotos, setQCPhotos] = useState<any[]>([]);
  const [uploadPhotos, setUploadPhotos] = useState<any[]>([]);
  const [elementAnswer, setElementAnswer] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showLoading, setShowLoading] = useState(false);
  const [currentImageCount, setCurrentImageCount] = useState(0);
  const [totalImageCount, setTotalImageCount] = useState(0);

  useEffect(() => {
    if (element) {
      if (element?.type === formElementTypes.photoRequest && element?.answers) {
        setPhotos(element?.answers?.inspector?.photoAnswers || []);
        setQCPhotos(element?.answers?.qc?.photoAnswers || []);
        setCanTakePhotos(element?.answers?.inspector?.answer ? true : false);
        setCanQCTakePhotos(element?.answers?.qc?.answer ? true : false);
      }
      setIsEdit(false);
    }
  }, [element]);

  const updateAnswer = (answer: any) => {
    if (element?.type === formElementTypes.photoRequest) {
      const unDeletedPhotos = answer.filter((ans: any) => !ans?.isDeleted);
      setUploadPhotos(unDeletedPhotos);
    } else {
      setElementAnswer(answer);
    }
  };

  const onSave = () => {
    setIsEdit(false);
    setQCReviewIsEdit(false);
    if (element?.type === formElementTypes.photoRequest) {
      if (canQCTakePhotos) {
        const payload = {
          photoAnswer: {
            failureExplanation: qcReason,
          },
        };
        updateImageAnswer(payload);
        setQCReason("");
      } else {
        imageUpload();
      }
    } else {
      updateAnswerRequest();
    }
  };

  const updateAnswerRequest = async () => {
    setIsLoading(true);
    const response = await updateElementAnswerRequest(
      orderId,
      element.id,
      elementAnswer
    );
    setIsLoading(false);
    if (response?.status === 204 || response?.status === 200) {
      dispatch(
        getOrderInspectionFormRequest({
          orderId: orderId,
        })
      );
    } else if (response?.status === 400) {
      setShowErrorNotification(true);
      setErrorMessage(response?.data?.detail);
    }
  };

  const imageUpload = async () => {
    if (uploadPhotos?.length === 0) {
      return;
    }

    const newImages: any[] = [];
    const exisitingImages: any[] = [];
    const inspectorPhotos = uploadPhotos.filter(
      (photo) => photo?.key && !photo?.isDeleted
    );
    const newPhotos = uploadPhotos.filter((photo) => !photo?.key);
    inspectorPhotos.forEach((photo, index) => {
      const data = {
        key: photo?.key,
        description: photo?.description,
        index: index,
      };
      exisitingImages.push(data);
    });
    setTotalImageCount(newPhotos.length || 0);
    for (let i = 0; i < newPhotos.length; i++) {
      if (newPhotos[i]?.file?.size <= 10000000) {
        setShowLoading(true);
        setCurrentImageCount(i + 1);
        try {
          const response = await uploadTempFileRequest(newPhotos[i]?.file);
          if (response && response.data) {
            setShowLoading(false);
            const data = {
              key: response.data,
              description: newPhotos[i]?.description,
              index: inspectorPhotos.length + i,
            };
            newImages.push(data);
          }
        } catch (error) {
          setShowLoading(false);
        }
      } else {
        setShowErrorNotification(true);
        setErrorMessage("File size exceeds 10MB limit.");
      }
    }

    if (newImages.length > 0 || exisitingImages) {
      const payload = {
        photoAnswer: {
          newItems: newImages,
          existingItems: exisitingImages,
        },
      };
      updateImageAnswer(payload);
    }
  };

  const updateImageAnswer = async (payload: any) => {
    setIsLoading(true);
    const response = await updateElementAnswerRequest(
      orderId,
      element.id,
      payload
    );
    setIsLoading(false);
    if (response?.status === 204 || response?.status === 200) {
      dispatch(
        getOrderInspectionFormRequest({
          orderId: orderId,
        })
      );
    } else if (response?.status === 400) {
      setShowErrorNotification(true);
      setErrorMessage(response?.data?.detail);
    }
  };

  const onToggleApproveElement = async (isApproved: boolean) => {
    const payload = {
      isApproved,
    };
    setIsLoading(true);
    const response = await elementApprovalRequest(orderId, element.id, payload);
    setIsLoading(false);
    if (response?.status === 204 || response?.status === 200) {
      dispatch(
        getOrderInspectionFormRequest({
          orderId: orderId,
        })
      );
    } else if (response?.status === 400 || response?.status === 403) {
      setShowErrorNotification(true);
      setErrorMessage(response?.data?.detail);
    }
  };

  const checkChildElementAnswer = () => {
    let answer = false;
    if (
      element?.type === formElementTypes.openEnd ||
      element?.type === formElementTypes.singleSelect ||
      element?.type === formElementTypes.multiSelect ||
      element?.type === formElementTypes.singleDate
    ) {
      answer = !!element?.answers?.qc?.answer;
    } else if (
      element?.type === formElementTypes.matrix ||
      element?.type === formElementTypes.multiInput ||
      element?.type === formElementTypes.table
    ) {
      element?.childElements?.forEach((child: any) => {
        if (child?.answers?.qc?.answer) {
          answer = true;
        }
      });
    } else if (element?.type === formElementTypes.photoRequest) {
      if (element?.answers?.qc) {
        answer = true;
      }
    }
    return answer;
  };

  const onDelete = async () => {
    setIsEdit(false);
    setQCReviewIsEdit(false);
    setIsLoading(true);
    const response = await deleteElementAnswerRequest(orderId, element.id);
    setIsLoading(false);
    if (response?.status === 204) {
      dispatch(
        getOrderInspectionFormRequest({
          orderId: orderId,
        })
      );
    } else if (response?.status === 400) {
      setShowErrorNotification(true);
      setErrorMessage(response?.data?.detail);
    }
  };

  return (
    <Box className={`question-margin question-layout`}>
      <Box
        display={"flex"}
        alignItems={"center"}
        justifyContent={"space-between"}
      >
        <Typography variant="h6" fontWeight={"510"}>
          {element?.settings?.title}
        </Typography>
        {isEdit ? (
          <Box>
            <Button
              sx={{ mr: 2 }}
              variant="text"
              startIcon={<DeleteOutlinedIcon />}
              color="error"
              onClick={onDelete}
            >
              Remove Edits
            </Button>
            <Button
              sx={{ mr: 2 }}
              variant="outlined"
              onClick={() => {
                setIsEdit(false);
                setQCReviewIsEdit(false);
              }}
            >
              Cancel
            </Button>
            <Button variant="contained" onClick={onSave}>
              Save
            </Button>
          </Box>
        ) : (
          [
            element?.type !== formElementTypes.textField && (
              <Box>
                {element?.isQcApproved ? (
                  <Chip
                    icon={<CheckCircleOutlinedIcon />}
                    sx={{ height: 36 }}
                    variant="filled"
                    label={"Approved"}
                    size="small"
                    color="success"
                    clickable
                    onClick={() => {
                      if (!qc2Completed) {
                        onToggleApproveElement(false);
                      }
                    }}
                  />
                ) : (
                  <>
                    <Button
                      sx={{
                        mr: 2,
                        color: "rgba(0, 0, 0, 0.87)",
                        borderColor: "#BDBDBD",
                        borderRadius: 5,
                      }}
                      variant="outlined"
                      onClick={() => onToggleApproveElement(true)}
                    >
                      Approve
                    </Button>
                    <Button
                      variant="text"
                      startIcon={<EditOutlinedIcon />}
                      onClick={() => {
                        setQCReviewIsEdit(true);
                        setIsEdit(true);
                      }}
                    >
                      Edit Response
                    </Button>
                  </>
                )}
              </Box>
            ),
          ]
        )}
      </Box>

      {element?.type === formElementTypes.textField ? (
        <RichTextReadOnly
          content={element?.settings?.text}
          extensions={extensions}
        />
      ) : (
        <Typography sx={{ mt: 0.5 }}>
          {element?.settings?.text || ""}
        </Typography>
      )}

      {isEdit && element?.type === formElementTypes.photoRequest && (
        <Box>
          <FormGroup sx={{ mt: 1 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={canQCTakePhotos}
                  onChange={(event) => setCanQCTakePhotos(event.target.checked)}
                  disabled={uploadPhotos.length > 0}
                />
              }
              label="Check if Unable to Take Photos"
            />
          </FormGroup>
          {canQCTakePhotos && (
            <TextField
              sx={{ mt: 1 }}
              fullWidth
              placeholder="Value"
              value={qcReason}
              onChange={(e) => setQCReason(e.target.value)}
            />
          )}
        </Box>
      )}

      {element?.type !== formElementTypes.textField && (
        <Box display={"flex"} mt={2}>
          <Typography
            sx={{
              minWidth: 100,
              color: "rgba(0, 0, 0, 0.87)",
              fontWeight: "550",
            }}
          >
            Inspector
          </Typography>
          {(element?.type === formElementTypes.openEnd ||
            element?.type === formElementTypes.singleSelect ||
            element?.type === formElementTypes.multiSelect ||
            element?.type === formElementTypes.singleDate) && (
            <Typography sx={{ fontStyle: "italic" }}>
              {element?.answers?.inspector?.answer || "-"}
            </Typography>
          )}
          {element?.type === formElementTypes.matrix && (
            <Box>
              {element?.childElements.map((element: any, index: number) => (
                <Box mt={index !== 0 ? 1 : 0}>
                  <Typography fontWeight={"550"}>{element?.title}</Typography>
                  <Typography sx={{ fontStyle: "italic" }}>
                    {element?.answers?.inspector?.answer || "n/a"}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
          {element?.type === formElementTypes.multiInput && (
            <Box sx={{ width: "100%" }}>
              <MultiInputPreview element={element} disabled={true} />
            </Box>
          )}
          {element?.type === formElementTypes.table && (
            <QuestionTable element={element} disabled={true} />
          )}
          {element?.type === formElementTypes.photoRequest && [
            canTakePhotos ? (
              <Box>
                <Typography fontWeight={"550"}>
                  Unable to Take Photos
                </Typography>
                <Typography>
                  {element?.answers?.inspector?.answer || ""}
                </Typography>
              </Box>
            ) : (
              <Box>
                {photos.length > 0 ? (
                  photos.map((photo) => (
                    <InspectorPhotosList photoData={photo} />
                  ))
                ) : (
                  <>-</>
                )}
              </Box>
            ),
          ]}
        </Box>
      )}

      {checkChildElementAnswer() ? (
        <Box display={"flex"} mt={2}>
          <Typography
            sx={{
              minWidth: 100,
              color: "rgba(0, 0, 0, 0.87)",
              fontWeight: "550",
            }}
          >
            Edited
          </Typography>
          {isEdit ? (
            <ElementField
              element={element}
              updateAnswer={updateAnswer}
              canQCTakePhotos={canQCTakePhotos}
            />
          ) : (
            <>
              {(element?.type === formElementTypes.openEnd ||
                element?.type === formElementTypes.singleSelect ||
                element?.type === formElementTypes.multiSelect ||
                element?.type === formElementTypes.singleDate) && (
                <Typography sx={{ fontStyle: "italic" }}>
                  {element?.answers?.qc?.answer || "-"}
                </Typography>
              )}
              {element?.type === formElementTypes.matrix && (
                <Box>
                  {element?.childElements.map((element: any, index: number) => (
                    <Box mt={index !== 0 ? 1 : 0}>
                      <Typography fontWeight={"550"}>
                        {element?.title}
                      </Typography>
                      <Typography sx={{ fontStyle: "italic" }}>
                        {element?.answers?.qc?.answer || "n/a"}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
              {element?.type === formElementTypes.multiInput && [
                checkChildElementAnswer() ? (
                  <Box sx={{ width: "100%" }}>
                    <MultiInputPreview
                      element={element}
                      disabled={true}
                      isQC={true}
                    />
                  </Box>
                ) : (
                  <>-</>
                ),
              ]}
              {element?.type === formElementTypes.table && [
                checkChildElementAnswer() ? (
                  <QuestionTable
                    element={element}
                    disabled={true}
                    isQC={true}
                  />
                ) : (
                  <>-</>
                ),
              ]}
              {element?.type === formElementTypes.photoRequest && [
                canQCTakePhotos ? (
                  <Box>
                    <Typography fontWeight={"550"}>
                      Unable to Take Photos
                    </Typography>
                    <Typography>
                      {element?.answers?.qc?.answer || ""}
                    </Typography>
                  </Box>
                ) : (
                  <Box>
                    {qcPhotos.length > 0 &&
                      qcPhotos.map((photo) => (
                        <InspectorPhotosList photoData={photo} />
                      ))}
                  </Box>
                ),
              ]}
            </>
          )}
        </Box>
      ) : (
        [
          isEdit && (
            <Box display={"flex"} mt={2}>
              <Typography
                sx={{
                  width: 100,
                  color: "rgba(0, 0, 0, 0.87)",
                  fontWeight: "550",
                }}
              >
                Edited
              </Typography>
              <ElementField
                element={element}
                updateAnswer={updateAnswer}
                canQCTakePhotos={canQCTakePhotos}
              />
            </Box>
          ),
        ]
      )}

      {showErrorNotification && (
        <ErrorNotification
          show={showErrorNotification}
          onClose={() => setShowErrorNotification(false)}
          title={errorMessage}
        />
      )}
      {isLoading && <Loading />}
      {showLoading && (
        <Loading
          title={`Uploading ${currentImageCount} of ${totalImageCount}`}
        />
      )}
    </Box>
  );
}
